/**
 * @fileoverview Shared validation for QA agent rulesets — the single source of
 * truth for "does this agent have enough valid rulesets to submit". Used by both
 * the wizard progress bar (per-scope step completion) and the AgentDetailsForm
 * submit gate so the two never disagree.
 * @owner @Appboy/intelligence
 */

import type { InteractionSite } from "./types";
import { INTERACTION_SITES, RULESET_SCOPES } from "./types";

/**
 * Minimal structural shape of a form ruleset. Kept local (rather than importing
 * AgentRuleset / CustomAgentFormValues) so `lib/agentConsole` stays free of a
 * dependency on the CustomAgentForm component. Fields are optional because form
 * state can hold partially-entered rulesets.
 */
interface RulesetLike {
  name?: string;
  rules?: (string | undefined)[];
  scope?: string;
}

/** A ruleset counts toward completion when it has a name and at least one non-blank rule. */
export const isValidRuleset = (ruleset: RulesetLike | undefined): boolean =>
  (ruleset?.name?.trim() ?? "").length > 0 &&
  Array.isArray(ruleset?.rules) &&
  ruleset.rules.some((rule) => (rule?.trim() ?? "").length > 0);

/**
 * Returns true when the agent's rulesets are sufficient to submit for the given
 * interaction site:
 * - Canvas QA: a valid ruleset in BOTH the `canvas` and `message_step` scopes,
 *   mirroring the wizard's per-scope step completion.
 * - Other QA sites (Campaign QA): at least one valid ruleset (scope is undefined).
 */
export const hasValidRulesetsForSite = (
  rulesets: (RulesetLike | undefined)[] | undefined,
  interactionSite: InteractionSite | undefined
): boolean => {
  if (!Array.isArray(rulesets)) {
    return false;
  }

  if (interactionSite === INTERACTION_SITES.CANVAS_QA) {
    const hasValidCanvasRuleset = rulesets.some(
      (ruleset) => ruleset?.scope === RULESET_SCOPES.CANVAS && isValidRuleset(ruleset)
    );
    const hasValidMessageStepRuleset = rulesets.some(
      (ruleset) => ruleset?.scope === RULESET_SCOPES.MESSAGE_STEP && isValidRuleset(ruleset)
    );
    return hasValidCanvasRuleset && hasValidMessageStepRuleset;
  }

  return rulesets.some(isValidRuleset);
};
